import React, { useCallback, memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TextField from '@mui/material/TextField';

import { isPositiveInt } from 'utils/validation';
import { inRange } from 'utils/number';
import {
  CONTRACT_NET_BASES,
  NEW_REV_MGMT_CONTRACT,
  REV_MGMT_CONTRACT_TYPES,
} from 'constants/revenueManagement';
import RegionsMultiSelect from 'components/regions/RegionsMultiSelect';
import SimpleSelect from 'components/common/SimpleSelect';
import { useNotification } from 'hooks/useNotifications';

import './index.scss';

const TRANSFORMERS = {
  regions: value => (Array.isArray(value) ? value.map(parseInt) : []),
  year: parseInt,
  commissionRate: parseInt,
  marketingDeduction: parseInt,
  yearGoal: parseInt,
  fixedAmount: parseInt,
  warrantySum: parseInt,
  q1GoalPercent: parseInt,
  q2GoalPercent: parseInt,
  q3GoalPercent: parseInt,
  q4GoalPercent: parseInt,
  netBase: value => CONTRACT_NET_BASES[value]?.key ?? null,
  contractType: value => REV_MGMT_CONTRACT_TYPES[value]?.key ?? null,
};

const VALIDATORS = {
  regions: value => !!(Array.isArray(value) && value.length),
  year: isPositiveInt,
  commissionRate: value => inRange(value, 0, 100),
  marketingDeduction: value => inRange(value, 0, 100),
  q1GoalPercent: value => inRange(value, 0, 100),
  q2GoalPercent: value => inRange(value, 0, 100),
  q3GoalPercent: value => inRange(value, 0, 100),
  q4GoalPercent: value => inRange(value, 0, 100),
  yearGoal: isPositiveInt,
  fixedAmount: isPositiveInt,
  warrantySum: isPositiveInt,
  netBase: Boolean,
  contractType: Boolean,
};

// 3 years before and after current
const START_YEAR = new Date().getFullYear() - 3;
const YEAR_OPTIONS = new Array(7).fill(1).map((_, index) => ({
  value: START_YEAR + index,
  option: `${START_YEAR + index}`,
}));

/**
 * Dialog to create/edit revenue management related contract details
 * Allows to create multiple contracts with same values for few regions
 */
function RevenueManagementDialog({ data, isNew, close, save }) {
  const changesRef = useRef({ ...data });
  const [errors, setErrors] = useState({});
  const [contractType, setContractType] = useState(
    REV_MGMT_CONTRACT_TYPES.normal.key,
  );
  const [netBase, setNetBase] = useState(CONTRACT_NET_BASES.net3.key);
  const { errorNotification: showError } = useNotification();

  const setField = useCallback(
    (name, value) => {
      if (!name) {
        return;
      }
      const transform = TRANSFORMERS[name];
      const validate = VALIDATORS[name];
      const preparedValue = transform ? transform(value) : value;
      const isValid = !validate || validate(preparedValue);
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (isValid) delete newErrors[name];
        else newErrors[name] = true;
        return newErrors;
      });
      changesRef.current[name] = preparedValue;
    },
    [changesRef, setErrors],
  );

  const handleContractTypeChange = useCallback(
    (_, value) => {
      if (!value || !changesRef.current) return;
      setContractType(value);
      setField('contractType', value);
    },
    [setContractType, setField],
  );

  const handleNetBaseChange = useCallback(
    (_, value) => {
      if (!value || !changesRef.current) return;
      setNetBase(value);
      setField('netBase', value);
    },
    [setNetBase, setField],
  );

  const handleRegionsChange = useCallback(
    regionIds => {
      console.info('handleRegionsChange', regionIds);
      setField('regions', regionIds);
    },
    [setField],
  );

  const handleBlur = useCallback(
    event => {
      const { name, value } = event.target || {};
      setField(name, value);
    },
    [setField],
  );

  const handleSave = useCallback(() => {
    const changes = { ...changesRef.current };
    const newErrors = {};
    Object.entries(VALIDATORS).forEach(([field, validate]) => {
      if (!validate(changes[field])) {
        newErrors[field] = true;
      }
    });
    console.info(changes);
    setErrors(newErrors);
    const errorKeys = Object.keys(newErrors);
    if (errorKeys.length) {
      showError(`Some fields are invalid:\n\n${errorKeys.join(',\n')}`);
      return;
    }
    save(changes);
  }, [save, changesRef, setErrors, showError]);

  return (
    <Dialog className="rev-mgmt-dialog" maxWidth="lg" open>
      <DialogTitle>
        {isNew ? 'Create contract entry' : 'Edit contract entry'}
      </DialogTitle>
      <DialogContent className="rev-mgmt-dialog__content">
        <Box className="rev-mgmt-dialog__form-wrapper">
          <Box sx={{ marginBottom: '12px' }}>
            <SimpleSelect
              label="Year"
              name="year"
              variant="outlined"
              defaultValue={data.year}
              optionList={YEAR_OPTIONS}
              onChange={handleBlur}
              error={!!errors.year}
            />
          </Box>
          <Box sx={{ marginBottom: '12px' }}>
            <RegionsMultiSelect
              error={!!errors.regions}
              regionIds={data.regions}
              readonly={!isNew}
              onChange={handleRegionsChange}
            />
          </Box>
          <Box sx={{ marginBottom: '8px' }}>
            <Box color="text.secondary" sx={{ marginBottom: '2px' }}>
              Choose a Contract type. &#34;Standalone&#34; means no algorithm
              applied
            </Box>
            <ToggleButtonGroup
              value={contractType}
              exclusive
              onChange={handleContractTypeChange}
            >
              {Object.values(REV_MGMT_CONTRACT_TYPES).map(({ key, label }) => (
                <ToggleButton key={key} value={key} className="toggle-button">
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ marginBottom: '16px' }}>
            <Box color="text.secondary" sx={{ marginBottom: '2px' }}>
              Choose a Net base to perform calculations
            </Box>
            <ToggleButtonGroup
              value={netBase}
              exclusive
              onChange={handleNetBaseChange}
            >
              {Object.values(CONTRACT_NET_BASES).map(({ key, label }) => (
                <ToggleButton key={key} value={key} className="toggle-button">
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box width={640}>
            <Box sx={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="commissionRate"
                  error={!!errors.commissionRate}
                  label="Commission Rate, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.commissionRate}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                  autoComplete="off"
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="marketingDeduction"
                  error={!!errors.marketingDeduction}
                  label="Marketing Deduction, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.marketingDeduction}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                  autoComplete="off"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="fixedAmount"
                  error={!!errors.fixedAmount}
                  label="Fixed Sum, CHF"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.fixedAmount}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0 } }}
                  autoComplete="off"
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="warrantySum"
                  error={!!errors.warrantySum}
                  label="Guaranteed Sum, CHF"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.warrantySum}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0 } }}
                  autoComplete="off"
                />
              </Box>
            </Box>
            <Box sx={{ marginBottom: '12px', display: 'flex' }}>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="yearGoal"
                  error={!!errors.yearGoal}
                  label="Yearly Goal, CHF"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.yearGoal}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0 } }}
                  autoComplete="off"
                />
              </Box>
            </Box>
            <Box sx={{ marginBottom: '12px', display: 'flex', gap: '4px' }}>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="q1GoalPercent"
                  error={!!errors.q1GoalPercent}
                  label="Q1 Goal, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.q1GoalPercent}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="q2GoalPercent"
                  error={!!errors.q2GoalPercent}
                  label="Q2 Goal, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.q2GoalPercent}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="q3GoalPercent"
                  error={!!errors.q3GoalPercent}
                  label="Q3 Goal, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.q3GoalPercent}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  name="q4GoalPercent"
                  error={!!errors.q4GoalPercent}
                  label="Q4 Goal, %"
                  type="number"
                  fullWidth
                  size="small"
                  variant="outlined"
                  defaultValue={data.q4GoalPercent}
                  onBlur={handleBlur}
                  slotProps={{ htmlInput: { min: 0, max: 100 } }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RevenueManagementDialog.propTypes = {
  data: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.number),
    year: PropTypes.number,
    commissionRate: PropTypes.number,
    marketingDeduction: PropTypes.number,
    yearGoal: PropTypes.number,
    fixedAmount: PropTypes.number,
    warrantySum: PropTypes.number,
    q1GoalPercent: PropTypes.number,
    q2GoalPercent: PropTypes.number,
    q3GoalPercent: PropTypes.number,
    q4GoalPercent: PropTypes.number,
    netBase: PropTypes.string,
    contractType: PropTypes.string,
  }),
  isNew: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
};

RevenueManagementDialog.defaultProps = {
  data: NEW_REV_MGMT_CONTRACT,
};

export default memo(RevenueManagementDialog);
